import React from 'react';
import { Home, Bath, Square, MapPin, Phone, Mail, ExternalLink, Calendar } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import ProfileImage from '../property/ProfileImage';

// URLs de Cloudflare
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Componente para imágenes de propiedades
const PropertyImage = ({ url, alt, className }) => {
  if (!url) return (
    <img
      src="/default-property-image.jpg"
      alt={alt}
      className={className}
    />
  );

  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      onError={(e) => {
        console.error('Error loading property image:', fullUrl);
        e.target.src = '/default-property-image.jpg';
      }}
    />
  );
};

const MinimalWhiteFlyer = ({ property }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Generate property URL - Corregido para usar operacion en lugar de tipo
  const getPropertyUrl = () => {
    const baseUrl = 'https://inkayros.com';
    const path = property.operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    return `${baseUrl}/${path}/${property.id}`;
  };

  const features = [
    { 
      icon: Home,
      label: 'Dormitorios',
      value: property.habitaciones
    },
    {
      icon: Bath,
      label: 'Baños',
      value: property.banos
    },
    {
      icon: Square,
      label: 'Área Total',
      value: `${property.area_total}m²`
    }
  ];

  // Additional features for a more comprehensive list
  const additionalFeatures = [
    { label: 'Estacionamiento', value: 'Sí' },
    { label: 'Jardín', value: 'Sí' },
    { label: 'Seguridad', value: '24/7' },
    { label: 'Antigüedad', value: property.antiguedad || 'Nueva' }
  ];

  return (
    <div className="max-w-2xl bg-white border border-gray-100 shadow-xl rounded-xl overflow-hidden">
      {/* Property Type Badge and Price Badge */}
      <div className="relative z-10">
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-medium shadow-sm">
          {property.operacion === 'alquiler' ? 'En Alquiler' : 'En Venta'}
        </div>
        
        {/* Price Badge - Movido a la esquina superior derecha */}
        <div className="absolute top-4 right-4 bg-white px-5 py-3 rounded-lg shadow-lg border-l-4 border-blue-600">
          <div className="text-sm font-medium text-blue-600 mb-1">
            {property.operacion === 'alquiler' ? 'Precio Alquiler' : 'Precio Venta'}
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            S/ {formatPrice(property.precio_soles)}
          </div>
          <div className="text-sm text-gray-600 font-medium">
            USD {formatPrice(property.precio_dolares)}
          </div>
        </div>
      </div>

      {/* Main Image Section */}
      <div className="relative h-96">
        <PropertyImage 
          url={property.imagenes[0]}
          alt="Main property view"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        {/* Bottom information overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {property.titulo}
            </h1>
            <div className="flex items-center gap-2 text-white/90">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">{property.ubicacion}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-8">
        {/* Description and Company */}
        <div className="flex gap-8 mb-8">
          <div className="flex-1">
            <p className="text-gray-700 leading-relaxed mb-4">
              {property.descripcion_corta || 'Una propiedad excepcional que combina elegancia y comodidad en cada detalle, diseñada para ofrecer la mejor experiencia de vida.'}
            </p>
          </div>
          <div className="flex flex-col items-center">
            <ProfileImage 
              url={property.vendedor?.foto_perfil}
              alt={property.vendedor?.nombre || "KAIROS INMOBILIARIA"}
              className="w-16 h-16 object-contain mb-2 rounded-full"
            />
            <div className="text-sm text-center text-gray-500">{property.vendedor?.nombre || "KAIROS INMOBILIARIA"}</div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {features.map((Feature, index) => (
            <div key={index} className="text-center p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-100">
              <Feature.icon className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 mb-1">{Feature.value}</div>
              <div className="text-sm text-gray-600">{Feature.label}</div>
            </div>
          ))}
        </div>

        {/* Gallery and QR Section */}
        <div className="flex gap-6 mb-8">
          {/* Gallery */}
          <div className="flex-1 grid grid-cols-2 gap-3">
            {property.imagenes.slice(1, 5).map((imagen, index) => (
              <div key={index} className="aspect-square rounded-lg overflow-hidden shadow-sm">
                <PropertyImage 
                  url={imagen}
                  alt={`Property view ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
          
          {/* QR Code and Additional Info */}
          <div className="w-1/3 flex flex-col items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="text-center mb-3">
              <QRCodeSVG 
                value={getPropertyUrl()} 
                size={120} 
                bgColor="#ffffff"
                fgColor="#000000"
                level="L"
              />
              <p className="text-xs text-gray-500 mt-2">Escanea para más información</p>
            </div>
          </div>
        </div>
        
        {/* Additional Features */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Características adicionales</h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {additionalFeatures.map((feature, index) => (
              <div key={index} className="flex justify-between border-b border-gray-100 pb-2">
                <span className="text-gray-600">{feature.label}</span>
                <span className="text-gray-900 font-medium">{feature.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="border-t border-gray-100 pt-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-50 p-2 rounded-full">
                <Phone className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Teléfono</div>
                <div className="text-gray-900 font-medium">{property.vendedor?.telefono || '+51 123 456 789'}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-blue-50 p-2 rounded-full">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Email</div>
                <div className="text-gray-900 font-medium">{property.vendedor?.email || 'contacto@inkayros.com'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 px-8 py-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          © {new Date().getFullYear()} InKayros Inmobiliaria
        </div>
        <div className="flex items-center gap-1">
          <ExternalLink className="w-4 h-4 text-blue-600" />
          <a href="#" className="text-sm text-blue-600 hover:text-blue-800 transition-colors">
            {getPropertyUrl()}
          </a>
        </div>
      </div>
    </div>
  );
};

export default MinimalWhiteFlyer;